import { InputWithLabel } from '@adotel/components'
import { useMutation } from '@apollo/client'
import DeleteIcon from '@material-ui/icons/Delete'
import { FormikErrors, FormikTouched } from 'formik'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import SectionTitle from '../../components/SectionTitle'
import { PROVIDER_DELETE_MUTATION } from '../../graphql/providerMutations'
import { numbersOnlyRegex, persianRegex } from '../../helpers/regex'
import { getTranslate, lang } from '../../localization'
import { Provider } from '../../types/provider'
import useStyles from './styles/ProviderBasicInfo.styles'

interface ProviderBasicInfoProps {
  values: Provider
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<Provider>
  touched: FormikTouched<Provider>
  handleBlur: {
    (e: React.FocusEvent<any, Element>): void
    <T = any>(fieldOrEvent: T): T extends string ? (e: any) => void : void
  }
  isSubmitting: boolean
  providerCode: string
}

const ProviderBasicInfo: React.FC<ProviderBasicInfoProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  providerCode
}) => {
  const classes = useStyles()
  const history = useHistory()
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)

  const [deleteProvider, { loading: loadingForProvider }] = useMutation(
    PROVIDER_DELETE_MUTATION,
    {
      onCompleted: (resp) => {
        toast.success(resp.deleteProvider.message)
        history.push(`/${lang}/provider/`)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const isPersian = persianRegex.test(values.nameTranslatedList[0].value)

  return (
    <div>
      <SectionTitle
        title={getTranslate('Basic Details')}
        marginBottom
        // deleteIcon={!!providerCode}
        //TODO: false means do not show
        deleteIcon={false}
        onClickOnDeleteIcon={() => {
          setDeleteModalIsOpen(true)
        }}
      />
      <div className={classes.inputsContainer}>
        <InputWithLabel
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the provider Name')}
          autoComplete="off"
          value={values.name}
          name="name"
          id="name"
          onChange={(event) => setFieldValue('name', event.currentTarget.value)}
          error={(touched.name && !!errors.name) || undefined}
          helpertext={
            touched.name ? getTranslate(errors.name || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Persian Name')}
          placeholder={getTranslate('Enter the provider Persian Name')}
          autoComplete="off"
          style={isPersian ? { fontFamily: 'IranSans' } : undefined}
          value={values.nameTranslatedList[0].value}
          name="nameTranslatedList"
          id="nameTranslatedList"
          onChange={(event) =>
            setFieldValue(
              'nameTranslatedList[0].value',
              event.currentTarget.value
            )
          }
          error={
            (touched.nameTranslatedList &&
              !values.nameTranslatedList[0].value) ||
            undefined
          }
          helpertext={
            touched.nameTranslatedList && !values.nameTranslatedList[0].value
              ? getTranslate('This field is required')
              : ''
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Code')}
          placeholder={getTranslate('Enter the provider Code')}
          autoComplete="off"
          value={providerCode ? values.displayProviderCode : values.code}
          name="code"
          id="code"
          onChange={(event) => {
            const rawInput = event.currentTarget.value
            const inputValue = rawInput.replace(/\s+/g, '') // remove all whitespace (including spaces, tabs)

            if (numbersOnlyRegex.test(inputValue) || inputValue === '') {
              setFieldValue('code', inputValue)
            }
          }}
          error={(touched.code && !!errors.code) || undefined}
          helpertext={
            touched.code ? getTranslate(errors.code || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || !!providerCode}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Slogan')}
          placeholder={getTranslate('Enter the Slogan')}
          autoComplete="off"
          value={values.slogan}
          name="slogan"
          id="slogan"
          onChange={(event) =>
            setFieldValue('slogan', event.currentTarget.value)
          }
          error={(touched?.slogan && !!errors?.slogan) || undefined}
          helpertext={
            touched?.slogan ? getTranslate(errors?.slogan || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Host Name')}
          placeholder={getTranslate('Enter the Host name')}
          autoComplete="off"
          value={values.hostName}
          name="hostName"
          id="hostName"
          onChange={(event) =>
            setFieldValue('hostName', event.currentTarget.value)
          }
          error={(touched?.hostName && !!errors?.hostName) || undefined}
          helpertext={
            touched?.hostName ? getTranslate(errors?.hostName || '') : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Rate Group Name')}
          placeholder={getTranslate('Enter rate group name')}
          autoComplete="off"
          value={values.rateGroupName}
          name="rateGroupName"
          id="rateGroupName"
          onChange={(event) =>
            setFieldValue('rateGroupName', event.currentTarget.value)
          }
          error={
            (touched?.rateGroupName && !!errors?.rateGroupName) || undefined
          }
          helpertext={
            touched?.rateGroupName
              ? getTranslate(errors?.rateGroupName || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Rate Group Persian Name')}
          placeholder={getTranslate('Enter the rate group Persian Name')}
          autoComplete="off"
          style={isPersian ? { fontFamily: 'IranSans' } : undefined}
          value={
            values.rateGroupNameTranslatedList.length > 0
              ? values.rateGroupNameTranslatedList[0].value
              : ''
          }
          name="rateGroupNameTranslatedList"
          id="rateGroupNameTranslatedList"
          onChange={(event) =>
            setFieldValue(
              'rateGroupNameTranslatedList[0].value',
              event.currentTarget.value
            )
          }
          error={
            (touched.rateGroupNameTranslatedList &&
              !values.rateGroupNameTranslatedList[0].value) ||
            undefined
          }
          helpertext={
            touched.rateGroupNameTranslatedList &&
            !values.rateGroupNameTranslatedList[0].value
              ? getTranslate('This field is required')
              : ''
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Rate Group Code')}
          placeholder={getTranslate('Enter rate group code')}
          autoComplete="off"
          value={values.rateGroupCode}
          name="rateGroupCode"
          id="rateGroupCode"
          onChange={(event) =>
            setFieldValue('rateGroupCode', event.currentTarget.value)
          }
          error={
            (touched?.rateGroupCode && !!errors?.rateGroupCode) || undefined
          }
          helpertext={
            touched?.rateGroupCode
              ? getTranslate(errors?.rateGroupCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
      </div>
      {deleteModalIsOpen && (
        <ConfirmActionModal
          icon={<DeleteIcon className={classes.icon} />}
          title={'The provider is about to be deleted'}
          subtitle={'Are you sure you want to delete it permanently?'}
          signature={'Delete Me'}
          onClose={() => setDeleteModalIsOpen(false)}
          isOpen={deleteModalIsOpen}
          onClick={() => {
            deleteProvider({
              variables: {
                providerCode: providerCode
              }
            })
          }}
          loading={loadingForProvider}
        />
      )}
    </div>
  )
}
export default ProviderBasicInfo
